import { ChevronDown, ChevronRight, Clock, MapPin } from "lucide-react";
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { Card, CardContent } from "components/ui/card";

import {
  formatClock,
  formatDayChip,
  formatDayHeading,
  formatEnd,
  groupAgendaByDay,
  placeLine,
  scheduleClockLabel,
  toDate,
  usableTimeZone,
  viewerClockHint,
} from "./agenda-schedule";
import { timezonePlace } from "../events/timezones";
import type { AgendaItem, Track } from "./schemas";

// DATA_MODEL.md defines this as the persisted default for tracks.
export const DEFAULT_TRACK_COLOR = "#6366f1";

/** Test ids and element ids use this in place of a date for the no-time group. */
const UNSCHEDULED_KEY = "unscheduled";

/**
 * The agenda as a schedule: one section per day, sessions in time order.
 *
 * Shared by the attendee Agenda tab and the admin Agenda tab so the two look
 * the same. Each passes its own controls through `renderActions` (a bookmark
 * for an attendee; reschedule, edit and delete for an organiser) and anything
 * that only it has to say through `renderNotice`. Everything else, from the
 * day headers to the order of two parallel sessions, is decided here once.
 *
 * Sessions with no start time get a last group, "No time set", when the
 * caller passes them. The admin tab does; the attendee tab filters them out
 * first, because a session with no time is a placeholder the organiser has
 * not finished.
 *
 * Days and times are on the event's clock, `timeZone`, and the line above the
 * schedule names it. Without a zone this browser can resolve they fall back
 * to the viewer's clock and the line says that instead; see
 * agenda-schedule.ts.
 *
 * On a phone the day headers stick under the app header while their day is
 * on screen. With `dayPicker`, a strip above the list jumps to a day, because
 * a three-day event is a long scroll at 390px; the attendee tab leaves it off
 * because its day filter does that job.
 *
 * Memoised, as are the grouping and each card: on the admin tab a dialog
 * sits beside a schedule of a hundred or more sessions, and a keystroke in it
 * must not re-render them. Callers pass stable `renderActions` and
 * `renderNotice` callbacks for the same reason.
 */
export const AgendaSchedule = memo(function AgendaSchedule({
  items,
  tracks,
  timeZone,
  idPrefix,
  renderActions,
  renderNotice,
  stackActionsOnPhone = false,
  collapsibleDays = true,
  dayPicker = true,
  highlightTrackIds,
}: {
  items: AgendaItem[];
  tracks: Track[];
  /** The event's IANA zone. */
  timeZone?: string;
  /** "agenda" on the attendee tab, "admin-agenda" on the admin one. */
  idPrefix: string;
  renderActions: (item: AgendaItem) => ReactNode;
  renderNotice?: (item: AgendaItem) => ReactNode;
  /** Several controls do not fit beside a title at 390px; put them under it. */
  stackActionsOnPhone?: boolean;
  /**
   * Whether a day's heading folds its sessions away. The admin tab does, to
   * keep a long schedule short; the attendee tab does not, because every
   * session is always listed there.
   */
  collapsibleDays?: boolean;
  /**
   * Whether a strip above the list jumps to a day. The admin tab has no day
   * filter, so it keeps the strip; the attendee tab has one and turns it off.
   */
  dayPicker?: boolean;
  /**
   * The attendee's own tracks. Their sessions are marked "Your track" so they
   * stand out in the full list. Pass a stable set; the admin tab passes none.
   */
  highlightTrackIds?: ReadonlySet<string>;
}) {
  const [collapsedDays, setCollapsedDays] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const [expandedItems, setExpandedItems] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const headerHeight = useAppHeaderHeight();

  const zone = usableTimeZone(timeZone);
  const { sections, clock, hint, place } = useMemo(() => {
    const schedule = groupAgendaByDay(items, zone);
    const starts = schedule.days.flatMap((day) =>
      day.items
        .map((item) => toDate(item.startsAt))
        .filter((date): date is Date => date !== null),
    );
    const place = zone ? timezonePlace(zone) : "";
    return {
      sections: [
        ...schedule.days.map((day) => ({
          key: day.key,
          heading: formatDayHeading(day.key),
          chip: formatDayChip(day.key),
          items: day.items,
        })),
        ...(schedule.unscheduled.length > 0
          ? [
              {
                key: UNSCHEDULED_KEY,
                heading: "No time set",
                chip: "No time set",
                items: schedule.unscheduled,
              },
            ]
          : []),
      ],
      clock: scheduleClockLabel(starts, zone),
      hint: zone ? viewerClockHint(starts, zone, place) : null,
      place,
    };
  }, [items, zone]);
  const trackById = useMemo(
    () => new Map(tracks.map((track) => [track.id, track])),
    [tracks],
  );
  const toggleExpanded = useCallback(
    (id: string) => setExpandedItems((current) => toggleSet(current, id)),
    [],
  );

  function jumpTo(key: string) {
    if (collapsedDays.has(key)) setCollapsedDays(toggleSet(collapsedDays, key));
    document
      .getElementById(`${idPrefix}-day-section-${key}`)
      ?.scrollIntoView({ block: "start", behavior: "smooth" });
  }

  return (
    <div className="space-y-6">
      {clock && (
        <div className="space-y-1 text-xs text-muted-foreground">
          <p
            className="flex items-center gap-1.5"
            data-testid={`${idPrefix}-clock`}
          >
            <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {zone
              ? `Times are in ${place} time (${clock}).`
              : `Times are in your time zone, ${clock}.`}
          </p>
          {hint && (
            <p className="pl-5" data-testid={`${idPrefix}-viewer-clock`}>
              {hint}
            </p>
          )}
        </div>
      )}

      {dayPicker && sections.length > 1 && (
        <nav
          aria-label="Jump to a day"
          className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1"
          data-testid={`${idPrefix}-day-picker`}
        >
          {sections.map((section) => (
            <button
              key={section.key}
              type="button"
              onClick={() => jumpTo(section.key)}
              className="min-h-11 shrink-0 whitespace-nowrap rounded-md border border-border bg-background px-3 py-2 text-sm font-semibold text-foreground transition-colors hover:border-lit focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
              data-testid={`${idPrefix}-jump-${section.key}`}
            >
              {section.chip}
              <span className="ml-2 font-normal text-muted-foreground">
                {section.items.length}
              </span>
            </button>
          ))}
        </nav>
      )}

      {sections.map((section) => {
        const isCollapsed = collapsibleDays && collapsedDays.has(section.key);
        const headingId = `${idPrefix}-day-heading-${section.key}`;
        const listId = `${idPrefix}-day-items-${section.key}`;
        return (
          <section
            key={section.key}
            id={`${idPrefix}-day-section-${section.key}`}
            aria-labelledby={headingId}
            style={{ scrollMarginTop: headerHeight + 8 }}
          >
            <h2
              className="sticky z-10 -mx-2 mb-3 border-b border-border bg-background/95 px-2 backdrop-blur-sm"
              style={{ top: headerHeight }}
            >
              {collapsibleDays ? (
                <button
                  id={headingId}
                  type="button"
                  onClick={() =>
                    setCollapsedDays(toggleSet(collapsedDays, section.key))
                  }
                  className={DAY_HEADING_CLASS}
                  aria-expanded={!isCollapsed}
                  aria-controls={listId}
                  data-testid={`${idPrefix}-day-${section.key}`}
                >
                  <DayHeadingText
                    section={section}
                    chevron={isCollapsed ? ChevronRight : ChevronDown}
                  />
                </button>
              ) : (
                <span
                  id={headingId}
                  className={DAY_HEADING_CLASS}
                  data-testid={`${idPrefix}-day-${section.key}`}
                >
                  <DayHeadingText section={section} />
                </span>
              )}
            </h2>

            {section.key === UNSCHEDULED_KEY && !isCollapsed && (
              <p className="mb-3 text-sm text-muted-foreground">
                These sessions have no start time, so they are not on any day
                yet.
              </p>
            )}

            {!isCollapsed && (
              <ol id={listId} className="space-y-3">
                {section.items.map((item) => (
                  <li key={item.id}>
                    <ScheduleItemCard
                      item={item}
                      timeZone={zone}
                      idPrefix={idPrefix}
                      trackById={trackById}
                      expanded={expandedItems.has(item.id)}
                      onToggleExpanded={toggleExpanded}
                      renderActions={renderActions}
                      renderNotice={renderNotice}
                      stackActionsOnPhone={stackActionsOnPhone}
                      highlighted={
                        highlightTrackIds !== undefined &&
                        (item.trackIds ?? []).some((id) =>
                          highlightTrackIds.has(id),
                        )
                      }
                    />
                  </li>
                ))}
              </ol>
            )}
          </section>
        );
      })}
    </div>
  );
});

const DAY_HEADING_CLASS =
  "flex w-full items-center justify-between gap-3 py-3 text-left";

function DayHeadingText({
  section,
  chevron: Chevron,
}: {
  section: { heading: string; items: AgendaItem[] };
  chevron?: typeof ChevronDown;
}) {
  return (
    <>
      <span className="font-display text-base font-semibold text-foreground md:text-lg">
        {section.heading}
      </span>
      <span className="flex shrink-0 items-center gap-2 text-sm text-muted-foreground">
        {section.items.length}{" "}
        {section.items.length === 1 ? "session" : "sessions"}
        {Chevron && <Chevron className="h-4 w-4" />}
      </span>
    </>
  );
}

export const ScheduleItemCard = memo(function ScheduleItemCard({
  item,
  timeZone,
  idPrefix,
  trackById,
  expanded,
  onToggleExpanded,
  renderActions,
  renderNotice,
  stackActionsOnPhone,
  highlighted,
}: {
  item: AgendaItem;
  timeZone: string | undefined;
  idPrefix: string;
  trackById: Map<string, Track>;
  expanded: boolean;
  onToggleExpanded: (id: string) => void;
  renderActions: (item: AgendaItem) => ReactNode;
  renderNotice?: (item: AgendaItem) => ReactNode;
  stackActionsOnPhone: boolean;
  highlighted: boolean;
}) {
  const tracks = (item.trackIds ?? [])
    .map((trackId) => trackById.get(trackId))
    .filter((track): track is Track => track !== undefined);
  const start = formatClock(item.startsAt, timeZone);
  const end = formatEnd(item, timeZone);
  const accent = tracks[0]?.color ?? (tracks[0] ? DEFAULT_TRACK_COLOR : null);

  return (
    <Card
      data-testid={`${idPrefix}-item-${item.id}`}
      data-highlighted={highlighted ? "true" : undefined}
      className={
        highlighted
          ? "border-l-4 border-border p-4 shadow-none dark:border-[length:var(--lit-edge-width)] dark:border-l-4 dark:border-lit-edge dark:shadow-lit sm:p-5"
          : "border-l-4 p-4 sm:p-5"
      }
      style={accent ? { borderLeftColor: accent } : undefined}
    >
      <CardContent
        className={
          stackActionsOnPhone
            ? "grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3 gap-y-2 p-0 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:gap-x-4"
            : "grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-x-3 p-0 sm:gap-x-4"
        }
      >
        <div
          className="min-w-16 whitespace-nowrap pt-0.5 tabular-nums"
          data-testid={`${idPrefix}-time-${item.id}`}
        >
          {start ? (
            <>
              <p className="text-sm font-semibold text-foreground">{start}</p>
              {end && (
                <p className="text-xs text-muted-foreground">
                  <span className="sr-only">until </span>
                  {end.clock}
                </p>
              )}
              {end && end.daysLater > 0 && (
                <p
                  className="text-xs font-semibold text-muted-foreground"
                  data-testid={`${idPrefix}-next-day-${item.id}`}
                >
                  <span aria-hidden>
                    +{end.daysLater} {end.daysLater === 1 ? "day" : "days"}
                  </span>
                  <span className="sr-only">
                    {end.daysLater === 1
                      ? "the next day"
                      : `${end.daysLater} days later`}
                  </span>
                </p>
              )}
            </>
          ) : (
            <p className="text-xs text-muted-foreground">No time</p>
          )}
        </div>

        <div className="min-w-0">
          <button
            type="button"
            onClick={() => onToggleExpanded(item.id)}
            // 44px on a phone even for a one-line title, with the title and
            // the chevron pinned to the top of it so they line up with the
            // time beside them, whatever the title's length; from `sm` up the
            // button is as tall as its text.
            className="flex w-full items-start justify-between gap-2 rounded-md text-left focus-visible:outline-hidden focus-visible:shadow-base max-sm:min-h-11"
            aria-expanded={expanded}
            aria-label={`${expanded ? "Collapse" : "Expand"} details for ${item.title}`}
          >
            <span className="min-w-0 break-words font-medium text-foreground">
              {item.title}
            </span>
            {expanded ? (
              <ChevronDown className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
            )}
          </button>

          <SessionPlace item={item} idPrefix={idPrefix} />

          {(tracks.length > 0 || highlighted) && (
            <div className="mt-2 flex flex-wrap gap-2">
              {highlighted && <YourTrackPill item={item} idPrefix={idPrefix} />}
              {tracks.map((track) => (
                <span
                  key={track.id}
                  className="rounded-full border px-3 py-1 text-xs font-semibold text-foreground"
                  style={{
                    borderColor: track.color ?? DEFAULT_TRACK_COLOR,
                    backgroundColor: `${track.color ?? DEFAULT_TRACK_COLOR}15`,
                  }}
                >
                  <span
                    className="mr-1.5 inline-block h-2 w-2 rounded-full"
                    style={{
                      backgroundColor: track.color ?? DEFAULT_TRACK_COLOR,
                    }}
                    aria-hidden
                  />
                  {track.name}
                </span>
              ))}
            </div>
          )}

          <SessionPresenters item={item} />

          {renderNotice?.(item)}

          {expanded && <SessionDetails item={item} idPrefix={idPrefix} />}
        </div>

        <div
          className={
            stackActionsOnPhone
              ? "col-span-2 flex justify-end gap-1 border-t border-border pt-2 sm:col-span-1 sm:items-start sm:border-t-0 sm:pt-0"
              : "flex items-start gap-1"
          }
        >
          {renderActions(item)}
        </div>
      </CardContent>
    </Card>
  );
});

/**
 * The height of the app's sticky header, so a day header can stick under it.
 *
 * Measured rather than hard-coded: below `sm` a page with a status badge or a
 * page action gets a second header row, and the heights differ between
 * breakpoints.
 */
function useAppHeaderHeight(): number {
  const [height, setHeight] = useState(0);
  useEffect(() => {
    const header = document.querySelector<HTMLElement>("[data-app-header]");
    if (!header) return;
    const update = () => setHeight(header.getBoundingClientRect().height);
    update();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(update);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);
  return height;
}

function toggleSet(current: ReadonlySet<string>, value: string): Set<string> {
  const next = new Set(current);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

/** Room and location, when the organiser filled either in. */
export function SessionPlace({
  item,
  idPrefix,
}: {
  item: AgendaItem;
  idPrefix: string;
}) {
  const place = placeLine(item);
  if (!place) return null;
  return (
    <p
      className="mt-1 flex items-start gap-1.5 text-sm text-muted-foreground"
      data-testid={`${idPrefix}-place-${item.id}`}
    >
      <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="min-w-0 break-words">{place}</span>
    </p>
  );
}

/** The marker on a session in the attendee's own track. */
export function YourTrackPill({
  item,
  idPrefix,
}: {
  item: AgendaItem;
  idPrefix: string;
}) {
  return (
    <span
      className="rounded-full bg-hb-dusk-deep px-3 py-1 text-xs font-semibold text-white"
      data-testid={`${idPrefix}-your-track-${item.id}`}
    >
      Your track
    </span>
  );
}

/**
 * The presenters' names and pictures. Renders the projection the trigger
 * maintains; do not resolve `presenterIds` here. An entry is keyed by
 * `attendeeId` or, since D14, by `speakerId` for a presenter who is not on
 * the roster. Both already carry the name and avatar, so this view does not
 * care which.
 */
export function SessionPresenters({ item }: { item: AgendaItem }) {
  if (!item.presenters || item.presenters.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1.5">
      {item.presenters.map((presenter) => (
        <span
          key={presenter.attendeeId ?? presenter.speakerId}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
        >
          {presenter.avatar && (
            <img
              src={presenter.avatar}
              alt=""
              className="h-5 w-5 rounded-md object-cover"
            />
          )}
          {presenter.name}
        </span>
      ))}
    </div>
  );
}

/** Whether a session has a description or location notes to open. */
export function hasSessionDetails(item: AgendaItem): boolean {
  return Boolean(item.locationNotes || item.description);
}

/** The organiser's note on where to find the session, when there is one. */
export function SessionLocationNotes({
  item,
  idPrefix,
}: {
  item: AgendaItem;
  idPrefix: string;
}) {
  if (!item.locationNotes) return null;
  return (
    <p
      className="mt-1 flex items-start gap-1.5 whitespace-pre-line text-sm text-muted-foreground"
      data-testid={`${idPrefix}-location-notes-${item.id}`}
    >
      <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="min-w-0 break-words">{item.locationNotes}</span>
    </p>
  );
}

/** The opened part of a session: location notes and the full description. */
export function SessionDetails({
  item,
  idPrefix,
}: {
  item: AgendaItem;
  idPrefix: string;
}) {
  return (
    <div
      className="mt-3 space-y-3 border-t border-border pt-3"
      data-testid={`${idPrefix}-details-${item.id}`}
    >
      {item.locationNotes && (
        <p className="flex items-start gap-1.5 whitespace-pre-line text-sm text-muted-foreground">
          <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="min-w-0 break-words">{item.locationNotes}</span>
        </p>
      )}
      {item.description && (
        <p className="whitespace-pre-line break-words text-sm text-muted-foreground">
          {item.description}
        </p>
      )}
      {!item.locationNotes && !item.description && (
        <p className="text-sm text-muted-foreground">
          No description for this session.
        </p>
      )}
    </div>
  );
}
